// Where the pages of a rasterized PDF begin and end (DT-06, Owner device test 2026-09-12).
//
// The PDF is a picture of the laid-out document, one image per page (src/report/pdf.ts). A page therefore
// ends at the bottom of a line of text — never through one — or at the top of a block that must not be
// split: a photo with its caption, a signature box, a table row, a heading kept with the line under it.
// Pure: pdf.ts measures the laid-out document, this file decides, vitest drives it
// (tests/report/pdfPagination.test.ts).
export interface PageSlice {
  start: number;
  end: number;
}

export interface ChunkSlice extends PageSlice {
  pages: PageSlice[];
}

export interface PaginationInput {
  // Height of the whole document, in layout pixels.
  total: number;
  // Height of one page's printable area, in layout pixels.
  pageHeight: number;
  // The bottom edge of every line of text.
  lineBottoms: readonly number[];
  // [top, bottom] of every block a page must not cut through.
  atomic: ReadonlyArray<readonly [number, number]>;
  // A page is not closed before this share of it is filled: a block too tall for the rest of the page
  // moves down whole, unless that would leave the page more empty than this — then it is cut. Default 0.5.
  minFill?: number;
}

export function paginate(input: PaginationInput): PageSlice[] {
  const total = Math.max(0, Math.ceil(input.total));
  const pageHeight = Math.max(1, Math.floor(input.pageHeight));
  const minFill = input.minFill ?? 0.5;
  const insideBlock = (y: number) => input.atomic.some(([top, bottom]) => y > top + 0.5 && y < bottom - 0.5);
  const candidates = [...input.lineBottoms, ...input.atomic.flatMap(([top, bottom]) => [top, bottom])]
    .map((y) => Math.round(y))
    .filter((y) => y > 0 && y < total && !insideBlock(y));
  const breaks = [...new Set(candidates)].sort((a, b) => a - b);
  const pages: PageSlice[] = [];
  let start = 0;
  while (start < total) {
    const limit = start + pageHeight;
    if (limit >= total) {
      pages.push({ start, end: total });
      break;
    }
    // nothing safe within reach: the page is cut at its edge
    let end = limit;
    for (const y of breaks) {
      if (y > limit) break;
      if (y > start && y >= start + pageHeight * minFill) end = y;
    }
    pages.push({ start, end });
    start = end;
  }
  return pages;
}

// Whole pages, in order, grouped so that no group is taller than `maxHeight` (one canvas per group).
export function chunkPages(pages: readonly PageSlice[], maxHeight: number): ChunkSlice[] {
  const chunks: ChunkSlice[] = [];
  for (const page of pages) {
    const last = chunks[chunks.length - 1];
    if (last && page.end - last.start <= maxHeight) {
      last.pages.push(page);
      last.end = page.end;
    } else {
      chunks.push({ start: page.start, end: page.end, pages: [page] });
    }
  }
  return chunks;
}
